import React, { useState } from 'react';
import axios from 'axios';
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader,
  Package,
  Users,
  UserCog,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Importacao = () => {
  const [tipoImportacao, setTipoImportacao] = useState('produtos');
  const [arquivo, setArquivo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const tiposImportacao = [
    {
      id: 'produtos',
      nome: 'Produtos',
      icon: Package,
      descricao: 'Importar cadastro de produtos com preços e estoque',
      color: 'text-orange-600',
      bg: 'bg-orange-100 dark:bg-orange-900/20'
    },
    {
      id: 'clientes',
      nome: 'Clientes',
      icon: Users,
      descricao: 'Importar cadastro de clientes com informações completas',
      color: 'text-blue-600',
      bg: 'bg-blue-100 dark:bg-blue-900/20'
    },
    {
      id: 'vendedores',
      nome: 'Vendedores',
      icon: UserCog,
      descricao: 'Importar cadastro de vendedores com comissões',
      color: 'text-purple-600',
      bg: 'bg-purple-100 dark:bg-purple-900/20'
    }
  ];

  const handleDownloadModelo = async () => {
    try {
      const response = await axios.get(`${API}/importacao/modelo/${tipoImportacao}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `modelo_${tipoImportacao}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Modelo baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao baixar modelo');
      console.error(error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
        return;
      }
      setArquivo(file);
      setResultado(null);
      toast.success(`Arquivo "${file.name}" selecionado`);
    }
  };

  const handleUpload = async () => {
    if (!arquivo) {
      toast.error('Selecione um arquivo primeiro');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', arquivo);

    try {
      const response = await axios.post(`${API}/importacao/upload/${tipoImportacao}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResultado(response.data);
      
      if (response.data.erros === 0) {
        toast.success(`✅ ${response.data.sucessos} registro(s) importado(s) com sucesso!`);
      } else {
        toast.warning(`⚠️ ${response.data.sucessos} sucesso(s), ${response.data.erros} erro(s)`);
      }
      
      // Limpar arquivo após upload
      setArquivo(null);
      document.getElementById('file-input').value = '';
      
    } catch (error) {
      toast.error('Erro ao importar arquivo');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const tipoAtual = tiposImportacao.find(t => t.id === tipoImportacao);
  const Icon = tipoAtual?.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-3 rounded-xl shadow-lg">
            <FileSpreadsheet className="w-8 h-8 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-heading font-bold text-foreground">Importação em Massa</h2>
            <p className="text-muted-foreground mt-1">Importe produtos, clientes e vendedores via Excel</p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">
              Como funciona?
            </h4>
            <ol className="text-sm text-blue-800 dark:text-blue-300 space-y-1 list-decimal list-inside">
              <li>Escolha o tipo de dados que deseja importar</li>
              <li>Baixe o modelo Excel correspondente</li>
              <li>Preencha o modelo com seus dados</li>
              <li>Faça o upload do arquivo preenchido</li>
              <li>Aguarde a importação ser processada</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Seleção do Tipo */}
      <div className="stat-card">
        <h3 className="font-heading font-semibold text-lg mb-4">1. Selecione o Tipo de Importação</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tiposImportacao.map((tipo) => {
            const TipoIcon = tipo.icon;
            const isActive = tipoImportacao === tipo.id;
            return (
              <button
                key={tipo.id}
                onClick={() => {
                  setTipoImportacao(tipo.id);
                  setArquivo(null);
                  setResultado(null);
                }}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  isActive
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-primary/50 bg-background'
                }`}
              >
                <div className={`p-3 rounded-lg ${tipo.bg} w-fit mb-3`}>
                  <TipoIcon className={`w-6 h-6 ${tipo.color}`} />
                </div>
                <h4 className={`font-semibold mb-1 ${isActive ? 'text-primary' : 'text-foreground'}`}>
                  {tipo.nome}
                </h4>
                <p className="text-sm text-muted-foreground">{tipo.descricao}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Download Modelo */}
      <div className="stat-card">
        <h3 className="font-heading font-semibold text-lg mb-4">2. Baixe o Modelo Excel</h3>
        <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
          <div className={`p-3 rounded-lg ${tipoAtual?.bg}`}>
            <Icon className={`w-8 h-8 ${tipoAtual?.color}`} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold mb-1">Modelo de {tipoAtual?.nome}</h4>
            <p className="text-sm text-muted-foreground">
              Planilha Excel com campos e exemplo de preenchimento
            </p>
          </div>
          <button
            onClick={handleDownloadModelo}
            className="btn-primary flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Baixar Modelo
          </button>
        </div>
      </div>

      {/* Upload do Arquivo */}
      <div className="stat-card">
        <h3 className="font-heading font-semibold text-lg mb-4">3. Faça Upload do Arquivo Preenchido</h3>
        
        <div className="space-y-4">
          {/* Área de Upload */}
          <div className="border-2 border-dashed border-border hover:border-primary rounded-lg p-8 text-center transition-all">
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="file-input"
              className="cursor-pointer"
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-semibold mb-2">
                {arquivo ? arquivo.name : 'Clique para selecionar o arquivo'}
              </p>
              <p className="text-sm text-muted-foreground">
                Arraste e solte ou clique para escolher (Excel .xlsx ou .xls)
              </p>
            </label>
          </div>

          {/* Botão de Upload */}
          {arquivo && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5" />
                  Importar Dados
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Resultado da Importação */}
      {resultado && (
        <div className="stat-card">
          <h3 className="font-heading font-semibold text-lg mb-4">Resultado da Importação</h3>
          
          {/* Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Total de Linhas</span>
              </div>
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {resultado.total_linhas}
              </p>
            </div>

            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">Sucessos</span>
              </div>
              <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                {resultado.sucessos}
              </p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm font-medium text-red-700 dark:text-red-300">Erros</span>
              </div>
              <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                {resultado.erros}
              </p>
            </div>
          </div>

          {/* Detalhes dos Sucessos */}
          {resultado.detalhes_sucessos && resultado.detalhes_sucessos.length > 0 && (
            <div className="mb-4">
              <h4 className="font-semibold text-green-700 dark:text-green-300 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Registros Importados ({resultado.detalhes_sucessos.length})
              </h4>
              <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                <div className="space-y-2">
                  {resultado.detalhes_sucessos.map((sucesso, idx) => (
                    <div key={idx} className="text-sm text-green-800 dark:text-green-200">
                      ✅ Linha {sucesso.linha}: {sucesso.nome}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Detalhes dos Erros */}
          {resultado.detalhes_erros && resultado.detalhes_erros.length > 0 && (
            <div>
              <h4 className="font-semibold text-red-700 dark:text-red-300 mb-2 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Erros Encontrados ({resultado.detalhes_erros.length})
              </h4>
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4 max-h-60 overflow-y-auto">
                <div className="space-y-2">
                  {resultado.detalhes_erros.map((erro, idx) => (
                    <div key={idx} className="text-sm text-red-800 dark:text-red-200">
                      ❌ Linha {erro.linha}: {erro.erro}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
